import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { ApiError } from './ApiError';
import type { ErroData } from './ApiError';
import { currentRoute, emitSessionExpired } from './sessionExpired';
import { recordRequestID } from '@app/lastRequest';
import { t } from '@app/i18n';

/** Envelope de sucesso do backend (`api-backend.md › Convenções`). */
interface SuccessEnvelope<T> {
  data: T;
  message: string;
  success: true;
  code: '';
}

/** Envelope de erro. */
interface ErrorEnvelope {
  data?: ErroData | null;
  message?: string;
  success: false;
  code: string;
}

type Envelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

function isEnvelope(body: unknown): body is Envelope<unknown> {
  return (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    typeof (body as { success: unknown }).success === 'boolean'
  );
}

/** Vazio = mesma origem (o nginx/Vite proxia `/api`). */
export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

/** Teto de qualquer chamada que não peça outro. */
export const TIMEOUT_DEFAULT_MS = 20_000;

/**
 * Teto das ordens que atravessam o middleware inteiro — discovery, materialize,
 * reconcile, restauro.
 *
 * 29/08, lab real: a **primeira** varredura do discovery passou de 20 s, o axios
 * abortou em 20 005 ms e o backend caiu junto (`context canceled` no log). O
 * padrão continua 20 s; quem sabe que a ordem é longa pede mais, por chamada
 * (`timeoutMs` em `typed.ts`, ou `timeout` no config do `request`).
 *
 * É o mesmo número que o indicador de tarefas anuncia («até 2 minutos»): se um mudar
 * sem o outro, a tela promete um limite que o cliente não respeita.
 */
export const TIMEOUT_ORDER_LONGAMS = 120_000;

/**
 * Teto das sete chamadas que leem a lista de APIs do gateway no executor (aim-h4n): a prévia e a confirmação de
 * Importar (T8), a reimportação e a religação de um recurso, a releitura dos agentes do gateway, a lista «Fora do
 * padrão» e o «Testar conexão» do APIM.
 *
 * A conta: o executor lê a lista em páginas e responde em até 40 s; o console espera 45 s, sem repetir, e a rota dele
 * responde até os 55 s (a leitura mais 10 s para a publicação do catálogo e a resposta). As bordas (o Ingress do
 * cluster e o nginx desta tela) cortam nos 60 s. Com 65 s, quem responde é sempre a rota ou a borda, e nunca o relógio
 * da tela. Os 20 s do padrão cortariam a leitura antes de o console responder com o código, e uma espera acima de 65 s
 * nunca chegaria a valer: a borda corta antes.
 */
export const GATEWAY_LIST_TIMEOUT_MS = 65_000;

export interface CreateApiClientOptions {
  baseURL?: string;
  /** Injetado nos testes; em produção o evento vai para o `window`. */
  onSessionExpired?: (detail: {
    retornoPara: string;
    code: 'sessao_expirada' | 'nao_autenticado';
  }) => void;
}

/**
 * Cliente HTTP do backend (T010).
 *
 * - `withCredentials`: o cookie de sessão `aim_session` é httpOnly.
 * - Interceptor de resposta: desembrulha `{data,message,success,code}` — o chamador
 *   recebe só o `data`; `success:false` (ainda que com HTTP 2xx) vira `ApiError`.
 * - Qualquer falha (rede, timeout, HTTP ≥ 400, corpo fora do envelope) vira `ApiError`,
 *   sempre com um `code` conhecido por `src/app/errors.ts`.
 * - 401 `sessao_expirada`/`nao_autenticado` dispara o evento global `session-expired`
 *   com a rota atual (AC-43b) antes de rejeitar.
 */
export function createApiClient(options: CreateApiClientOptions = {}): AxiosInstance {
  const notify = options.onSessionExpired ?? emitSessionExpired;

  const instance = axios.create({
    baseURL: options.baseURL ?? API_BASE_URL,
    withCredentials: true,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    // Escritas com middleware podem levar até 15 s no backend; damos folga de rede.
    // Ordem que sabidamente passa disso pede `timeoutMs` na própria chamada.
    timeout: TIMEOUT_DEFAULT_MS,
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // O card de erro de rota precisa de um identificador para o log do backend.
      recordRequestID(response.headers?.['x-request-id'] as string | undefined);
      const body: unknown = response.data;

      if (!isEnvelope(body)) {
        // 204 e afins não têm corpo — tratamos como `data: null`.
        if (response.status === 204 || body === '' || body === undefined || body === null) {
          response.data = null;
          return response;
        }
        throw new ApiError({
          code: 'resposta_invalida',
          message: t('error.resposta_invalida.client'),
          status: response.status,
        });
      }

      if (body.success) {
        response.data = body.data;
        return response;
      }

      throw toApiError(body, response.status);
    },
    (error: unknown) => {
      const apiError = normalizeFailure(error);
      if (apiError.status === 401 && apiError.isSessionExpired) {
        notify({
          retornoPara: currentRoute(),
          code: apiError.code === 'nao_autenticado' ? 'nao_autenticado' : 'sessao_expirada',
        });
      }
      return Promise.reject(apiError);
    },
  );

  return instance;
}

function toApiError(body: ErrorEnvelope, status: number): ApiError {
  return new ApiError({
    code: body.code || 'erro_desconhecido',
    // Sem default: quem escolhe o texto é `errors.ts`, por `code`. Um default
    // aqui vira `serverMessage` e passa por mensagem **do servidor** — a frase
    // genérica reapareceria interpolada dentro de outra frase.
    message: body.message ?? '',
    status,
    data: body.data ?? null,
  });
}

/** Traduz qualquer falha do axios (ou o `ApiError` já lançado acima) em `ApiError`. */
export function normalizeFailure(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<unknown>;
    const body = axiosError.response?.data;

    if (isEnvelope(body) && !body.success) {
      return toApiError(body, axiosError.response?.status ?? 0);
    }

    if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
      return new ApiError({
        code: 'tempo_esgotado',
        message: t('error.tempo_esgotado.client'),
        status: axiosError.response?.status ?? 0,
      });
    }

    if (!axiosError.response) {
      return new ApiError({
        code: 'sem_conexao',
        message: t('error.sem_conexao.client'),
        status: 0,
      });
    }

    // Houve resposta e ela não é envelope. `axiosError.message` aqui é
    // "Request failed with status code 502" — texto do transporte, não do
    // servidor: pô-lo em `serverMessage` faria a tela mostrá-lo como se fosse a
    // explicação (o `erro_desconhecido` interpola `{mensagem}` desde 28/08).
    // Vazio deixa a mensagem honesta do `errors.ts` valer, e o detalhe técnico
    // fica no console, que é onde ele serve.
    return new ApiError({
      code: 'erro_desconhecido',
      message: '',
      status: axiosError.response.status,
    });
  }

  return new ApiError({
    code: 'erro_desconhecido',
    message: '',
    status: 0,
  });
}

/** Instância única usada pelos hooks de react-query. */
export const api: AxiosInstance = createApiClient();

/** Faz uma chamada e devolve só o `data` do envelope, já tipado. */
export async function request<T>(
  config: AxiosRequestConfig,
  client: AxiosInstance = api,
): Promise<T> {
  const response = await client.request<T>(config);
  return response.data;
}
